import React from 'react';
import Icon from '@/components/ui/Icon';

export type SaveErrorKind = 'insufficient_licenses' | 'program_excluded' | 'generic';

export interface FailedProgram {
  id?: number;
  message: string;
  name: string;
}

interface SaveErrorBannerProps {
  failedPrograms?: FailedProgram[];
  kind: SaveErrorKind;
}

const COPY: Record<SaveErrorKind, { body: string; header: string }> = {
  generic: {
    body: "This program couldn't be added to the classroom. "
      + 'Please try again or contact info@teachtci.com for assistance.',
    header: 'Something Went Wrong',
  },
  insufficient_licenses: {
    body: "There aren't enough licenses to assign this program. "
      + 'Contact your account representative to purchase additional licenses.',
    header: 'Not Enough Available Licenses',
  },
  program_excluded: {
    body: 'The program is excluded from auto-rostering. '
      + 'Update program settings, or contact info@teachtci.com for assistance.',
    header: 'Program Excluded from Rostering',
  },
};

const SingleFailureBanner = ({ failure, kind }: { failure: FailedProgram; kind: SaveErrorKind }) => {
  const { header } = COPY[kind];

  return (
    <>
      <div className="tw-font-bold tw-text-base tw-leading-6">
        {header}
      </div>
      <div className="tw-text-sm tw-text-very-dark-grey tw-leading-[22px]">
        <span className="tw-font-semibold">{failure.name}</span>
        {' — '}
        {failure.message}
      </div>
    </>
  );
};

const MultiFailureBanner = ({ failures }: { failures: FailedProgram[] }) => (
  <>
    <div className="tw-font-bold tw-text-base tw-leading-6">
      {COPY.generic.header}
    </div>
    <ul className="tw-text-sm tw-text-very-dark-grey tw-list-disc tw-pl-5 tw-mt-1 tw-mb-0">
      {failures.map(program => (
        <li key={program.id ?? program.name}>
          <span className="tw-font-semibold">{program.name}</span>
          {' — '}
          {program.message}
        </li>
      ))}
    </ul>
  </>
);

const SaveErrorBanner = ({ failedPrograms, kind }: SaveErrorBannerProps) => {
  const hasProgramDetails = failedPrograms && failedPrograms.length > 0;
  const isMulti = hasProgramDetails && failedPrograms.length > 1;

  return (
    <div
      className={`
        tw-flex tw-items-start tw-bg-red/5 tw-text-red
        tw-border tw-border-solid tw-border-red tw-rounded-[10px]
        tw-px-3 tw-py-3 tw-my-3
      `}
      role="alert"
    >
      <Icon
        className="tw-mr-3 tw-flex-shrink-0"
        icon="Warning"
        size="24"
      />
      <div>
        {isMulti && <MultiFailureBanner failures={failedPrograms} />}
        {hasProgramDetails && !isMulti && <SingleFailureBanner failure={failedPrograms[0]} kind={kind} />}
        {!hasProgramDetails && (
          <>
            <div className="tw-font-bold tw-text-base tw-leading-6">
              {COPY[kind].header}
            </div>
            <div className="tw-text-sm tw-text-very-dark-grey tw-leading-[22px]">
              {COPY[kind].body}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SaveErrorBanner;
